import {
  AlertTriangle,
  ArrowDown01,
  ArrowDownAZ,
  ChevronDown,
  ChevronRight,
  Loader2,
  Trash2
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import {
  UploadFileCard,
  type UploadStatus
} from "@/components/upload/UploadFileCard";
import { cn } from "@/lib/utils";

import type {
  ConflictPolicy,
  StagingMatch
} from "../../caseFiles/api/filesystemApi";
import type { StagedFile } from "../../caseFiles/upload/useStagedUpload";

/** What the card says about a staged file, in priority order. */
export type CardState =
  | "staging"
  | "failed"
  | "unsupported"
  | "duplicate"
  | "conflict"
  | "possible-duplicate"
  | "ready";

export type RailSort = "time" | "name";

/**
 * The first staged file with a given hash is the original; later ones with the
 * same bytes are duplicates of it (the server only sees one request at a time,
 * so this covers separate drops into one open modal).
 */
export const batchOriginalFor = (
  file: StagedFile,
  files: StagedFile[]
): StagedFile | null => {
  if (!file.sha256) return null;
  const twin = files.find(
    (other) =>
      other.id !== file.id &&
      other.sha256 === file.sha256 &&
      other.status !== "unsupported" &&
      other.addedAt < file.addedAt
  );
  return twin ?? null;
};

/** A folder in the staged tree, derived from each file's `relativePath`. */
type StagedTreeFolder = {
  name: string;
  /** Full path from the drop root, e.g. `"Awards/2024"`. */
  path: string;
  folders: StagedTreeFolder[];
  files: StagedFile[];
};

/**
 * Groups staged files into the folder tree the user dropped, keyed off each
 * file's `relativePath`. Files with no folder segment sit at the root, so a
 * plain multi-file drop stays a flat list.
 */
const buildStagedTree = (files: StagedFile[]): StagedTreeFolder => {
  const root: StagedTreeFolder = { name: "", path: "", folders: [], files: [] };
  for (const file of files) {
    const segments = file.relativePath.split("/").filter(Boolean);
    const folderSegs = segments.slice(0, -1); // the last segment is the filename
    let cur = root;
    let path = "";
    for (const seg of folderSegs) {
      path = path ? `${path}/${seg}` : seg;
      let next = cur.folders.find((f) => f.name === seg);
      if (!next) {
        next = { name: seg, path, folders: [], files: [] };
        cur.folders.push(next);
      }
      cur = next;
    }
    cur.files.push(file);
  }
  return root;
};

/** Every file at or below a folder, for counts, status and cascade removal. */
const descendantFiles = (folder: StagedTreeFolder): StagedFile[] => [
  ...folder.files,
  ...folder.folders.flatMap(descendantFiles)
];

const earliestAddedAt = (folder: StagedTreeFolder): number =>
  Math.min(...descendantFiles(folder).map((f) => f.addedAt));

/** Sorts files and subfolders in place by the rail's current sort. */
const sortStagedTree = (folder: StagedTreeFolder, sort: RailSort): void => {
  folder.files.sort((a, b) =>
    sort === "name"
      ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      : a.addedAt - b.addedAt
  );
  folder.folders.sort((a, b) =>
    sort === "name"
      ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      : earliestAddedAt(a) - earliestAddedAt(b)
  );
  folder.folders.forEach((f) => sortStagedTree(f, sort));
};

type FolderStatus = "staging" | "attention" | "ready";

export const cardStateOf = (
  file: StagedFile,
  files: StagedFile[]
): CardState => {
  if (file.status === "staging") return "staging";
  if (file.status === "failed") return "failed";
  if (file.status === "unsupported") return "unsupported";
  if (file.check?.duplicates.length || batchOriginalFor(file, files))
    return "duplicate";
  if (file.check?.name_conflict) return "conflict";
  if (file.check?.possible_duplicates.length) return "possible-duplicate";
  return "ready";
};

/** Rolls the per-file states up to a single folder-level indicator. */
const folderStatusOf = (
  descendants: StagedFile[],
  allFiles: StagedFile[]
): FolderStatus => {
  const states = descendants.map((f) => cardStateOf(f, allFiles));
  if (states.some((s) => s === "staging")) return "staging";
  if (
    states.some(
      (s) =>
        s === "duplicate" ||
        s === "conflict" ||
        s === "possible-duplicate" ||
        s === "failed"
    )
  )
    return "attention";
  return "ready";
};

/** "Exhibits / B — Passport.pdf" from "/Exhibits/B — Passport.pdf". */
export const displayPath = (path: string) =>
  path.split("/").filter(Boolean).join(" / ");

const CONFLICT_CHOICES: { value: ConflictPolicy; label: string }[] = [
  { value: "keep_both", label: "Keep both" },
  { value: "replace", label: "Replace" },
  { value: "skip", label: "Skip" }
];

type CardProps = {
  file: StagedFile;
  state: CardState;
  original: StagedFile | null;
  decision: ConflictPolicy;
  targetName: string;
  selected: boolean;
  selectable: boolean;
  /** Nesting depth in the folder tree; indents the card. */
  depth?: number;
  /** Shown under a folder, so the folder-path subtitle is redundant. */
  inTree?: boolean;
  onSelect: () => void;
  onDecide: (policy: ConflictPolicy) => void;
  onRemove: () => void;
  onRetry: () => void;
  onShow: (match: StagingMatch) => void;
};

const StagedCard: FC<CardProps> = ({
  file,
  state,
  original,
  decision,
  targetName,
  selected,
  selectable,
  depth = 0,
  inTree = false,
  onSelect,
  onDecide,
  onRemove,
  onRetry,
  onShow
}) => {
  const folder = file.relativePath.includes("/")
    ? file.relativePath.slice(0, file.relativePath.lastIndexOf("/"))
    : null;
  const firstDuplicate = file.check?.duplicates[0] ?? null;
  const extraDuplicates = Math.max(0, (file.check?.duplicates.length ?? 0) - 1);
  const possible = file.check?.possible_duplicates[0] ?? null;
  // Everything that finished staging is added unless the user removes it; only
  // the types that cannot be added read as muted.
  const excluded = state === "unsupported" || state === "failed";

  const subtitle = (() => {
    switch (state) {
      case "staging":
        return `Uploading… ${file.progress}%`;
      case "failed":
        return file.error ?? "Upload failed";
      case "unsupported":
        return "Unsupported type";
      case "duplicate":
        if (firstDuplicate) {
          return (
            <>
              Duplicate of {displayPath(firstDuplicate.path)}
              {extraDuplicates ? ` +${extraDuplicates} more` : ""}
              {" · "}
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => onShow(firstDuplicate)}
              >
                Show
              </button>
            </>
          );
        }
        return original ? `Same file as ${original.name} above` : "Duplicate";
      case "conflict":
        return `A file named ${
          file.check?.name_conflict?.name ?? file.name
        } is already in ${targetName}`;
      case "possible-duplicate":
        return possible ? (
          <>
            Same name and size as {displayPath(possible.path)}
            {" · "}
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => onShow(possible)}
            >
              Show
            </button>
          </>
        ) : null;
      default:
        return inTree ? "Ready" : folder ?? "Ready";
    }
  })();

  const status: UploadStatus =
    state === "staging"
      ? "busy"
      : state === "failed"
      ? "error"
      : state === "unsupported"
      ? "muted"
      : state === "duplicate" || state === "conflict"
      ? "warn"
      : state === "possible-duplicate"
      ? "info"
      : "ready";

  return (
    <UploadFileCard
      id={file.id}
      name={file.name}
      state={state}
      status={status}
      subtitle={subtitle}
      subtitleTone={
        state === "failed"
          ? "danger"
          : state === "duplicate" || state === "conflict"
          ? "warn"
          : "muted"
      }
      excluded={excluded}
      selected={selected}
      onSelect={selectable ? onSelect : undefined}
      depth={depth}
      onRemove={onRemove}
      trailing={
        state === "failed" ? (
          <Button variant="danger" size="xs" onClick={onRetry}>
            Retry
          </Button>
        ) : null
      }
    >
      {state === "conflict" ? (
        <div
          className="relative z-10 mt-2 inline-flex rounded-md border border-line-2 p-0.5 text-sm"
          role="radiogroup"
          aria-label={`What to do with ${file.name}`}
        >
          {CONFLICT_CHOICES.map((choice) => (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={decision === choice.value}
              className={cn(
                "rounded px-2 py-0.5",
                decision === choice.value
                  ? "bg-primary text-primary-foreground"
                  : "text-fg-3 hover:bg-fill-1"
              )}
              onClick={() => onDecide(choice.value)}
            >
              {choice.label}
            </button>
          ))}
        </div>
      ) : null}
    </UploadFileCard>
  );
};

type FolderRowProps = {
  name: string;
  depth: number;
  count: number;
  status: FolderStatus;
  collapsed: boolean;
  onToggle: () => void;
  onRemove: () => void;
};

const StagedFolderRow: FC<FolderRowProps> = ({
  name,
  depth,
  count,
  status,
  collapsed,
  onToggle,
  onRemove
}) => (
  <li
    className="group relative rounded-lg border border-line-2 bg-elevated px-3 py-2.5 shadow-sm animate-in fade-in slide-in-from-top-2"
    style={depth ? { marginLeft: depth * 16 } : undefined}
    data-testid="staged-folder"
    data-folder-collapsed={collapsed || undefined}
  >
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? `Expand ${name}` : `Collapse ${name}`}
        aria-expanded={!collapsed}
        className="flex size-6 shrink-0 items-center justify-center rounded text-fg-3 hover:bg-fill-1"
      >
        {collapsed ? (
          <ChevronRight className="size-4" />
        ) : (
          <ChevronDown className="size-4" />
        )}
      </button>
      <FileTypeIcon isFolder size={16} />
      <div className="min-w-0 flex-1">
        <div
          className="max-w-full truncate text-base font-medium text-fg"
          title={name}
        >
          {name}
        </div>
        <div className="truncate text-sm text-fg-3">
          {count} {count === 1 ? "file" : "files"}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {status === "staging" ? (
          <Loader2 className="size-4 animate-spin text-fg-3" />
        ) : status === "attention" ? (
          <AlertTriangle className="size-4 text-warn" />
        ) : null}
        <Button
          variant="ghost"
          size="icon-xs"
          className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={`Remove folder ${name}`}
          onClick={onRemove}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  </li>
);

type Props = {
  files: StagedFile[];
  targetName: string;
  decisions: Record<string, ConflictPolicy>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDecide: (id: string, policy: ConflictPolicy) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  onShow: (match: StagingMatch) => void;
  /** Host overrides for the root (e.g. to fill a sidebar edge-to-edge). */
  className?: string;
};

/**
 * Every staged file is added unless the user removes it; only the states that
 * cannot be committed (still uploading, failed, or an unsupported type) are out.
 */
export const isIncluded = (state: CardState) =>
  state !== "staging" && state !== "failed" && state !== "unsupported";

/**
 * The rail: every staged file as a numbered card with its status, duplicate /
 * conflict hints and a hover Remove. Every file is added unless it is removed;
 * name conflicts still carry a Keep both / Replace / Skip choice.
 */
export const StagedFilesRail: FC<Props> = ({
  files,
  targetName,
  decisions,
  selectedId,
  onSelect,
  onDecide,
  onRemove,
  onRetry,
  onShow,
  className
}) => {
  const [sort, setSort] = useState<RailSort>("time");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLUListElement>(null);
  const [overflows, setOverflows] = useState(false);

  const tree = useMemo(() => {
    const root = buildStagedTree(files);
    sortStagedTree(root, sort);
    return root;
  }, [files, sort]);

  const toggleFolder = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const update = () =>
      setOverflows(
        el.scrollHeight > el.clientHeight + 4 &&
          el.scrollTop + el.clientHeight < el.scrollHeight - 4
      );
    update();
    el.addEventListener("scroll", update);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [files.length, collapsed]);

  const readyCount = files.filter((f) =>
    isIncluded(cardStateOf(f, files))
  ).length;

  /**
   * Renders a folder's subfolders (as rows) then its files (as cards), each
   * indented by `depth`. Root files render at depth 0 exactly as a flat list.
   */
  const renderFolder = (
    folder: StagedTreeFolder,
    depth: number
  ): ReactNode[] => {
    const nodes: ReactNode[] = [];
    for (const sub of folder.folders) {
      const isCollapsed = collapsed.has(sub.path);
      const descendants = descendantFiles(sub);
      nodes.push(
        <StagedFolderRow
          key={`folder:${sub.path}`}
          name={sub.name}
          depth={depth}
          count={descendants.length}
          status={folderStatusOf(descendants, files)}
          collapsed={isCollapsed}
          onToggle={() => toggleFolder(sub.path)}
          onRemove={() => descendants.forEach((f) => onRemove(f.id))}
        />
      );
      if (!isCollapsed) nodes.push(...renderFolder(sub, depth + 1));
    }
    for (const file of folder.files) {
      const state = cardStateOf(file, files);
      nodes.push(
        <StagedCard
          key={file.id}
          file={file}
          state={state}
          original={batchOriginalFor(file, files)}
          decision={decisions[file.id] ?? "keep_both"}
          targetName={targetName}
          selected={file.id === selectedId}
          selectable={state !== "unsupported"}
          depth={depth}
          inTree={depth > 0}
          onSelect={() => onSelect(file.id)}
          onDecide={(policy) => onDecide(file.id, policy)}
          onRemove={() => onRemove(file.id)}
          onRetry={() => onRetry(file.id)}
          onShow={onShow}
        />
      );
    }
    return nodes;
  };

  return (
    <div
      className={cn("flex h-full min-h-0 flex-col gap-3", className)}
      data-testid="staged-rail"
    >
      <div className="overflow-hidden rounded-xl bg-elevated shadow-sm ring-1 ring-line-2">
        <div className="flex w-full items-center justify-between gap-2 border-b border-line-1 bg-fill-1 px-3 py-2">
          <span className="text-sm font-medium text-fg-3">Ready to Add</span>
          <span className="text-sm text-fg-3">
            {readyCount} {readyCount === 1 ? "file" : "files"}
          </span>
        </div>
        <div className="flex w-full items-center justify-between gap-3 px-3 py-2">
          <span className="text-sm font-medium text-fg-3">Sort</span>
          <Button
            variant="ghost"
            size="xs"
            className="text-fg-3"
            onClick={() => setSort((s) => (s === "time" ? "name" : "time"))}
            aria-label={
              sort === "time" ? "Sorted by upload time" : "Sorted by name"
            }
            data-testid="staged-rail-sort"
          >
            {sort === "time" ? (
              <ArrowDown01 className="size-3" />
            ) : (
              <ArrowDownAZ className="size-3" />
            )}
            {sort === "time" ? "Upload Time" : "Name"}
          </Button>
        </div>
      </div>
      <div className="relative min-h-0 flex-1">
        <ul
          ref={listRef}
          className="no-scrollbar flex h-full flex-col gap-2 overflow-y-auto px-1 py-1"
        >
          {renderFolder(tree, 0)}
        </ul>
        {overflows ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-10 items-end justify-center bg-gradient-to-t from-background/90 to-transparent pb-1">
            <ChevronDown className="size-4 animate-bounce text-fg-3" />
          </div>
        ) : null}
      </div>
    </div>
  );
};
